import React, { useContext, useEffect, useState } from 'react';
import { createManageDomainBreadcrumbs } from '../Utils/BreadcrumbUtils';
import { Breadcrumb } from '../Components/Breadcrumb';
import CustomSearchBar from 'Components/GMS/Products/components/CustomSearchBar';
import { CREATE_DOMAIN, CREATE_DOMAIN_PERMISSION_MESSAGE, CREATE_PERMISSION, DELETE_PERMISSION, READ_PERMISSION, UPDATE_DOMAIN_PERMISSION_MESSAGE, UPDATE_PARTY_PERMISSION_MESSAGE, UPDATE_PERMISSION } from '../Utils/Constants';
import DomainTable from './DomainTable';
import { CreateDomainDialog } from './CreateDomainDialog';
import { useHistory } from 'react-router-dom';
import DetailSection from './DetailSection';
import { TParty } from '../Types/party.types';
import { TCreateDomainPayload, TIndustry, TIndustryUpdatePayload } from '../Types/domain.types';
import { toast } from 'react-toastify';
import { CreatePartyDialog } from '../Party/CreatePartyDialog';
import Paginator from 'Components/Common/PaginatorWithApi';
import { TPaginationParams } from 'Pages/GMS/Performance/analytics';
import NoDataComponent from 'Components/Common/NoDataComponent';
import { AppContext } from 'Context/AppContext';
import AppSettings from 'Settings/AppSettings';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';

type TDomainType = {
    partyId?: string;
}

const ManageDomain = ({ partyId }: TDomainType) => {
    const history = useHistory();
    const appContext = useContext(AppContext);
    const api_base_url = AppSettings.GMS_BACKEND_API_URL;

    const readPermission = appContext.pm.checkPermission(READ_PERMISSION);
    const createPermission = appContext.pm.checkPermission(CREATE_PERMISSION);
    const updatePermission = appContext.pm.checkPermission(UPDATE_PERMISSION);
    const deletePermission = appContext.pm.checkPermission(DELETE_PERMISSION);

    const [partyData, setPartyData] = useState<TParty | null>(null);
    const [domains, setDomains] = useState<TIndustry[]>([]);

    const [isCreateDomainOpen, setIsCreateDomainOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    
    const [isEditPartyOpen, setIsEditPartyOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [isCreatingDomain, setIsCreatingDomain] = useState(false);
    const [isDomainsLoading, setIsDomainsLoading] = useState(true);

    const [paginationParams, setPaginationParams] = useState<TPaginationParams>({pageNumber: 1, pageSize: 10});
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [refreshDomainTable, setRefreshDomainTable] = useState(0);
    
    const [editDomainDialog, setEditDomainDialog] = useState<{
        isOpen: boolean;
        domain: TIndustry | null;
    }>({ isOpen: false, domain: null });
    const [isUpdatingDomain, setIsUpdatingDomain] = useState(false);

    useEffect(() => {
        const fetchPartyData = async () => {
            if (!partyId) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const partyDataResponse = await ServiceWrapper.doGet( `${api_base_url}user/party/list/${partyId}`, null, null, AuthManager.getGmsToken(),); 
                const response = partyDataResponse?.data;
                if (response && response.done && response.data && response.data.length > 0) {
                    setPartyData(response.data[0]);
                } else {
                    console.error('No party data found');
                    toast.error('Failed to load party details');
                }
            } catch (error) {
                console.error('Error fetching party data:', error);
                toast.error('Error loading party details');
            } finally {
                setIsLoading(false);
            }
        };

        if (!readPermission) {
            history.push("/workspace/module/permission/denied");
            return;
        } else {
            fetchPartyData();
        }
    }, [partyId]);

    useEffect(() => {
        if (refreshDomainTable > 0) {
            setIsDomainsLoading(true);
        }
    }, [refreshDomainTable]);

    const handleCreateDomain = async (formData: any) => {
        if (!createPermission) {
            toast.warn(CREATE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }    
        if (!partyId) return;
        
        setIsCreatingDomain(true);
        
        try {
            const payload: TCreateDomainPayload = {
                name: formData.domainName,
                description: formData.description || '',
                active: true
            };
            
            const domainResponse = await ServiceWrapper.doPost( 
                `${api_base_url}party/${partyId}/industry`, 
                payload, 
                null, 
                AuthManager.getGmsToken()
            );
            const response = domainResponse?.data;
            if (response && response.done) {
                toast.success('Domain created successfully!');
                setIsCreateDomainOpen(false);
                
                setRefreshDomainTable(prev => prev + 1);
                
                setPaginationParams(prev => ({ ...prev, pageNumber: 1 }));
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to create domain');
            }
        } catch (error) {
            console.error('Error creating domain:', error);
            toast.error('An error occurred while creating the domain');
        } finally {
            setIsCreatingDomain(false);
        }
    };

    // useEffect(() => {
    //     const unlisten = history.listen((location, action) => {
    //         if (action === 'POP') {
    //             if (partyId) {
                    
    //             }
    //         }
    //     });
        
    //     return () => {
    //         unlisten();
    //     };
    // }, [history, partyId]);

    const handleUpdateDomain = async (formData: any) => {
        if (!updatePermission) {
            toast.warn(UPDATE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }
        
        const domain = editDomainDialog.domain;
        if (!domain) return;
        
        setIsUpdatingDomain(true);
        
        try {
            const payload: TIndustryUpdatePayload = {
                active: true,
                description: formData.description || '',
                id: domain.id,
                name: formData.domainName,
                sams_id: domain.sams_id
            };
            
            const domainResponse = await ServiceWrapper.doPost( 
                `${api_base_url}industry/edit/${domain.id}`, 
                payload, 
                null, 
                AuthManager.getGmsToken()
            );
            const response = domainResponse?.data;
            
            if (response && response.done) {
                toast.success('Domain updated successfully!');
                setEditDomainDialog({ isOpen: false, domain: null });
                
                setRefreshDomainTable(prev => prev + 1);
                
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update domain');
            }
        } catch (error) {
            console.error('Error updating domain:', error);
            toast.error('An error occurred while updating the domain');
        } finally {
            setIsUpdatingDomain(false);
        }
    };

    const handleEditDomain = (domain: TIndustry, index: number) => {
        if (!updatePermission) {
            toast.warn(UPDATE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }
        setEditDomainDialog({
            isOpen: true,
            domain: domain
        });
    };

    const handleNavigateToChannel = (domain: TIndustry) => {
        history.push(`/admin/party/${partyId ?? ''}/domain/${domain?.id ?? ''}/channel`);
    };

    const handleNavigateToParties = () => {
        history.push('/admin/party/manage');
    };

    const breadcrumbItems = partyData 
        ? createManageDomainBreadcrumbs(partyData.name, handleNavigateToParties)
        : [];

    const handleEditParty = () => {
        if (!updatePermission) {
            toast.warn(UPDATE_PARTY_PERMISSION_MESSAGE);
            return;
        }  
        setIsEditPartyOpen(true);
    };

    const handleUpdateParty = async (formData: any) => {
        if (!updatePermission) {
            toast.warn(UPDATE_PARTY_PERMISSION_MESSAGE);
            return;
        } 
        if (!partyData || !partyId) return;
        
        setIsUpdating(true);
        
        try {
            const partyResponse = await ServiceWrapper.doPost( 
                `${api_base_url}party/edit/${partyId}`, 
                formData, 
                null, 
                AuthManager.getGmsToken()
            );
            const response = partyResponse?.data;
            if (response && response.done) {
                toast.success('Party updated successfully!');
                
                setPartyData({
                    ...partyData,
                    name: formData.name,
                    address: formData.address,
                    party_type: formData.party_type
                });
                
                setIsEditPartyOpen(false);
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update party');
            }
        } catch (error) {
            console.error('Error updating party:', error);
            toast.error('An error occurred while updating the party');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setPaginationParams(prev => ({ ...prev, pageNumber: 1 }));
    };

    const handlePageChange = (pageNumber: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageNumber: pageNumber,
        }));
    };
        
    const updatePageSize = (pageSize: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageSize: pageSize,
            pageNumber: 1
        }));
    };

    if (isLoading) {
        return (
            <div className="tw-flex tw-justify-center tw-items-center tw-h-64">
                <div className="tw-inline-flex tw-items-center">
                    <svg className="tw-animate-spin tw-h-5 tw-w-5 tw-mr-3 tw-text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading party details...
                </div>
            </div>
        );
    }

    if (!partyData) {
        return (
            <div className="tw-text-center tw-py-8 tw-text-gray-500">
                Party not found
            </div>
        );
    }

    const onCreateDomainClick = () => {
        if (!createPermission) {
            toast.warn(CREATE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }
        setIsCreateDomainOpen(true);
    }

    return (
        <>
            <Breadcrumb items={breadcrumbItems} />
            <DetailSection 
                config={{
                    type: 'party',
                    data: {
                        title: partyData.name,
                        partyType: partyData.party_type || 'N/A',
                        address: partyData.address || 'N/A'
                    },
                    onEdit: handleEditParty
                }}
            />
            {!isDomainsLoading && domains.length === 0 ? (
                <>
                    <NoDataComponent title='No Domain found' />
                    <div className="tw-flex tw-justify-center">
                        <button onClick={onCreateDomainClick} className={`button-style tw-w-max tw-flex tw-justify-center`}>
                            {CREATE_DOMAIN}
                        </button>
                    </div>
                </>
            ) : (
                <>
                    {domains.length !== 0 && (
                        <div className={`tw-flex tw-items-center tw-justify-between tw-mt-2`}>
                            <div className='tw-w-[25%] tw-mt-4'>
                                <CustomSearchBar 
                                    handleSearchChange={handleSearchChange} 
                                    placeholder='Search domains' 
                                />
                            </div>
                            <button onClick={onCreateDomainClick} className={`button-style tw-w-max`}>
                                {CREATE_DOMAIN}
                            </button>
                        </div>
                    )}
                    {partyId && (
                        <DomainTable 
                            partyId={partyId}
                            searchTerm={searchTerm}
                            showLoader={showLoader}
                            domains={domains}
                            setDomains={setDomains}
                            setShowLoader={setShowLoader}
                            setTotalRecords={setTotalRecords}
                            paginationParams={paginationParams}
                            refreshTrigger={refreshDomainTable}
                            onDomainsLoaded={() => setIsDomainsLoading(false)}
                            onEditDomain={handleEditDomain}
                            onNavigateToChannel={handleNavigateToChannel}
                            updatePermission={updatePermission}
                            deletePermission={deletePermission}
                        />
                    )}
                </>
            )}
            {!showLoader && domains.length !== 0 && totalRecords > 0 ?
                <div id="pagination-container" className="tw-py-2">
                    <div className={`tw-flex tw-justify-end tw-items-center tw-space-x-3 tw-flex-wrap`}>
                        <div className={`tw-flex tw-items-center tw-space-x-3`}>
                            <span>Show</span>
                            <select
                                id="page-size-select"
                                className={`tw-border focus:tw-outline-none tw-p-1`}
                                style={{ width: "80px" }}
                                aria-label="Records on page select"
                                value={paginationParams?.pageSize ?? 10}
                                onChange={(e) => {
                                    updatePageSize(parseInt(e?.target?.value));
                                }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                            </select>
                        </div>
                        <Paginator
                            onPageChange={handlePageChange}
                            recordPerPage={paginationParams?.pageSize}
                            presentPage={paginationParams?.pageNumber}
                            totalRecords={totalRecords} 
                        />
                    </div>
                </div> : null}
            
            <CreateDomainDialog 
                isOpen={isCreateDomainOpen}
                onClose={() => setIsCreateDomainOpen(false)}
                onSave={handleCreateDomain}
                isLoading={isCreatingDomain}
            />
            
            <CreateDomainDialog
                isOpen={editDomainDialog.isOpen}
                onClose={() => setEditDomainDialog({ isOpen: false, domain: null })}
                onSave={handleUpdateDomain}
                isLoading={isUpdatingDomain}
                editData={editDomainDialog.domain}
                mode="edit"
            />
            
            <CreatePartyDialog
                isOpen={isEditPartyOpen}
                onClose={() => setIsEditPartyOpen(false)}
                onSave={handleUpdateParty}
                isLoading={isUpdating}
                editData={partyData}
                mode="edit"
            />
        </>
    );
}

export default ManageDomain;